import React from "react";
import ReactDOM from "react-dom/client";
import CfcTournament from "./CfcTournament";
import NoResult from "../../../../public/javascripts/reactcomponents/NoResult";
import { SPORT_TYPE } from "../../enums.js";

/** @typedef {import('../../transformers/CfcTournamentTransformer.js').CfcTournamentObjForApi } CfcTournamentObjForApi */

/**
 * @typedef {object} CfcTournamentsSearchProps
 * @property {Array<CfcTournamentObjForApi>} cfcTournaments - Liste des compétitions
 * @property {Array<CfcTournamentObjForApi>} currentCfcTournaments - Liste des compétitions en cours
 * @property {boolean} fetchDone - Indique si la récupération des compétitions est terminée
 * @property {object} search - Paramètres de recherche
 * @property {?('functional_fitness'|'hybrid_race'|'trail')} search.sportType - Type de sport des compétitions à récupérer
 * @property {?string} search.name - Nom partiel pour filtrer les compétitions
 * @property {boolean} search.isPassed - Indique si on veut récupérer les compétitions passées
 * @property {boolean} search.cashPrice - Indique si on veut récupérer les compétitions avec cash price
 */

class CfcTournamentsSearch extends React.Component {
  /**
   * @param {CfcTournamentsSearchProps} props
   */
  constructor(props) {
    super(props);
    this.sportTypeButtonsRef = React.createRef();
    this.state = {
      cfcTournaments: /** @type {Array<CfcTournamentObjForApi>} */ ([]),
      currentCfcTournaments: /** @type {Array<CfcTournamentObjForApi>} */ ([]),
      fetchDone: false,
      search: {
        sportType: this.getSportTypeFromUrl(),
        name: null,
        nameLike: null,
        isPassed: false,
        cashPrice: false
      }
    };
  }

  async componentDidMount() {
    const cfcTournaments = await this.fetchCfcTournaments();
    const currentCfcTournaments = await this.fetchCurrentCfcTournaments();

    this.setState({ cfcTournaments, currentCfcTournaments, fetchDone: true });
  }

  getSportTypeFromUrl() {
    const SPORT_TYPES = Object.keys(SPORT_TYPE).map(sportType => sportType.toLowerCase());
    const sportType = new URLSearchParams(window.location.search).get("sporttype");

    return sportType && SPORT_TYPES.includes(sportType) ? sportType : null;
  }

  async refreshCfcTournaments() {
    var cfcTournaments = await this.fetchCfcTournaments();
    this.setState({ cfcTournaments, fetchDone: true });
  }

  /**
   * Lors du clic sur un bouton de type de sport, on met à jour l'état de recherche.
   * @param {React.MouseEvent<any>} event
   */
  onClickOnButtonSportType = event => {
    const buttonNode = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const sportType = buttonNode.getAttribute("data-sport-type");
    const isAlreadyPushed = buttonNode.classList.contains("btn--pushed");
    let formattedSportType = null;

    if (this.sportTypeButtonsRef.current) {
      const sportTypeButtons = this.sportTypeButtonsRef.current.querySelectorAll("button[data-sport-type]");

      for (const sportTypeButton of sportTypeButtons) {
        sportTypeButton.classList.remove("btn--pushed");
      }
    }

    if (!isAlreadyPushed) {
      buttonNode.classList.add("btn--pushed");
      formattedSportType = sportType === "all" ? null : sportType;
    }

    this.setState(
      {
        search: {
          ...this.state.search,
          sportType: formattedSportType
        }
      },
      () => {
        this.refreshCfcTournaments();
      }
    );
  };

  /**
   * Lors du clic sur le bouton "Passé", on bascule l'état de recherche pour inclure ou exclure les compétitions passées.
   * @param {React.MouseEvent<any>} event
   * @returns {void}
   */
  onClickOnButtonPassed = event => {
    const buttonNode = /** @type {HTMLButtonElement} */ (event.currentTarget);

    buttonNode.classList.toggle("btn--pushed");

    this.setState(
      {
        search: {
          ...this.state.search,
          isPassed: !this.state.search.isPassed
        }
      },
      () => {
        this.refreshCfcTournaments();
      }
    );
  };

  /**
   * Lors du clic sur le bouton "Cash Price", on bascule l'état de recherche pour inclure ou exclure les compétitions avec cash price.
   * @param {React.MouseEvent<any>} event
   */
  onClickOnButtonCashPrice = event => {
    const buttonNode = /** @type {HTMLButtonElement} */ (event.currentTarget);

    buttonNode.classList.toggle("btn--pushed");
    this.setState(
      {
        search: {
          ...this.state.search,
          cashPrice: !this.state.search.cashPrice
        }
      },
      () => {
        this.refreshCfcTournaments();
      }
    );
  };

  /**
   * @param {any} event
   */
  onChangeOnInputSearch = event => {
    const inputNode = /** @type {HTMLInputElement} */ (event.currentTarget);
    const name = inputNode.value;

    this.setState(
      {
        search: {
          ...this.state.search,
          name: name || null
        }
      },
      () => {
        this.refreshCfcTournaments();
      }
    );
  };

  /**
   * Permet de récupérer la liste des compétitions
   * @returns {Promise<Array<CfcTournamentObjForApi>>} - Liste des compétitions
   */
  async fetchCfcTournaments() {
    let apiUrl = `/api/cfc-tournament/search`;
    const sportType = this.state.search.sportType;
    const name = this.state.search.name;
    const isPassed = this.state.search.isPassed;
    const cashPrice = this.state.search.cashPrice;
    const queryStrArray = [];

    if (sportType) {
      queryStrArray.push(`sporttype=${sportType}`);
    }

    if (name) {
      queryStrArray.push(`name=${name}`);
    }

    if (isPassed) {
      queryStrArray.push(`status=closed`);
    }

    if (cashPrice) {
      queryStrArray.push(`cashprice=true`);
    }

    if (queryStrArray.length > 0) {
      apiUrl += `?${queryStrArray.join("&")}`;
    }

    var response = await fetch(apiUrl, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  async fetchCurrentCfcTournaments() {
    const response = await fetch("/api/cfc-tournament/search?status=live", {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  render() {
    return (
      <div className="flexline flexline--direction-column flexline--gap-32">
        <div className="search search--none-shadow">
          <div className="search__container">
            <div className="grid grid--has-3-child grid--16-gap" ref={this.sportTypeButtonsRef} style={{ gridTemplateColumns: "auto 1fr auto" }}>
              <div className="grid grid--has-4-child grid--has-2-child-mobile no-mobile grid--16-gap">
                <button className={!this.state.search.sportType ? "btn btn--pushed" : "btn"} data-sport-type="all" onClick={this.onClickOnButtonSportType}>
                  Toutes
                </button>
                <button className={this.state.search.sportType === "functional_fitness" ? "btn btn--pushed" : "btn"} data-sport-type="functional_fitness" onClick={this.onClickOnButtonSportType}>
                  Functional Fitness
                </button>
                <button className={this.state.search.sportType === "hybrid_race" ? "btn btn--pushed" : "btn"} data-sport-type="hybrid_race" onClick={this.onClickOnButtonSportType}>
                  Hybrid Race
                </button>
                <button className={this.state.search.sportType === "trail" ? "btn btn--pushed" : "btn"} data-sport-type="trail" onClick={this.onClickOnButtonSportType}>
                  Trail
                </button>
              </div>

              <div className="grid grid--has-1-child grid--16-gap">
                <div className="input-group input-group--no-margin">
                  <input className="input-group__input" type="text" placeholder="Rechercher une compétition..." aria-label="Rechercher une compétition" onChange={this.onChangeOnInputSearch} />
                </div>
              </div>

              <div className="grid grid--has-2-child grid--has-2-child-mobile no-mobile grid--16-gap">
                <button className="btn" onClick={this.onClickOnButtonPassed}>
                  <span className="material-symbols-outlined">clock_arrow_down</span>
                  Passé
                </button>
                <button className="btn btn--warning" onClick={this.onClickOnButtonCashPrice}>
                  <i className="material-icons">attach_money</i>
                  Cash Price
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="container container--fixed-width">
          {this.state.currentCfcTournaments.length > 0 && (
            <>
              <div className="grid no-mobile grid--16-gap grid--has-3-child grid--product-mobile padding--16 background--light-grey" style={{ borderRadius: "8px" }}>
                {this.state.currentCfcTournaments.map((cfcTournament, index) => (
                  <CfcTournament key={`${cfcTournament.registrationLink}-${index}`} cfcTournament={cfcTournament} mode="small" />
                ))}
              </div>
              <div className="space--32"></div>
            </>
          )}

          {!this.state.fetchDone ? null : this.state.cfcTournaments.length > 0 ? (
            <div className="grid no-mobile grid--has-4-child grid--product-mobile">
              {this.state.cfcTournaments.map((cfcTournament, index) => (
                <CfcTournament key={`${cfcTournament.registrationLink}-${index}`} cfcTournament={cfcTournament} mode="full" />
              ))}
            </div>
          ) : (
            <NoResult title="Aucune compétition trouvée" message="La prochaine démarre bientôt. En attendant, explorez le calendrier ou revoyez les derniers résultats." />
          )}
        </div>
      </div>
    );
  }
}

var cfcTournamentsSearchNode = document.getElementById("cfc-tournaments-search");
if (cfcTournamentsSearchNode) {
  var root = ReactDOM.createRoot(cfcTournamentsSearchNode);
  root.render(<CfcTournamentsSearch />);
}
